import { useCallback, useEffect, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import { ThreatProtectionView, type ThreatBan, type ThreatDetail, type ThreatExclusion, type ThreatForm, type ThreatIP, type ThreatProtectionViewProps, type ThreatSection, type ThreatSource } from '@workspace/silk-ui';
import { useSession } from '@/context/SessionContext';
import { useNavigation } from '@/context/NavigationContext';
import { createThreatAPI, validIP, validNetwork } from './api';
import { canManageThreatProtection } from './access';

export default function ThreatProtectionPage({ section }: { section: ThreatSection }) {
  const { activeTenant, loading: scopeLoading } = useScope();
  const { session, loading } = useSession();
  const navigation = useNavigation();
  const [settledTenant, setSettledTenant] = useState(activeTenant?.id);
  const switchingTenant = settledTenant !== activeTenant?.id;
  useEffect(() => {
    if (!switchingTenant) return;
    if (settledTenant) navigation.navigate(`/threat-protection/${section}`, { replace: true });
    setSettledTenant(activeTenant?.id);
  }, [activeTenant?.id, navigation, section, settledTenant, switchingTenant]);
  if (switchingTenant) return <div role="status" className="p-6 text-sm text-muted-foreground">Switching tenant…</div>;
  if (loading || scopeLoading) return <div role="status" className="p-6 text-sm text-muted-foreground">Loading tenant access…</div>;
  if (!activeTenant?.id) return <div className="p-6 text-sm text-muted-foreground">Select a tenant to open Threat Protection.</div>;
  if (!canManageThreatProtection(session, activeTenant.id)) return <div role="alert" className="p-6 text-sm text-muted-foreground">Threat Protection must be enabled for this tenant and requires tenant administration permission.</div>;
  // Scope changes destroy all data, forms, in-flight work and selected evidence.
  return <ThreatProtectionWorkspace key={`${session.id}:${activeTenant.id}:${section}`} tenant={activeTenant.id} section={section} />;
}

const message = (err: unknown) => err instanceof Error ? err.message : 'Request failed. Try again.';
export function ThreatProtectionWorkspace({ tenant, section }: { tenant: string; section: ThreatSection }) {
  const navigation = useNavigation();
  const [ips, setIPs] = useState<ThreatIP[]>([]);
  const [bans, setBans] = useState<ThreatBan[]>([]);
  const [sources, setSources] = useState<ThreatSource[]>([]);
  const [allowlist, setAllowlist] = useState<ThreatExclusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const cursor = useRef('');
  const [detail, setDetail] = useState<ThreatDetail | null>(null);
  const [selectedIP, setSelectedIP] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailTab, setDetailTab] = useState<'summary' | 'evidence' | 'history'>('summary');
  const [form, setForm] = useState<ThreatForm | null>(null);
  const [formError, setFormError] = useState('');
  const [exclusionIP, setExclusionIP] = useState('');
  const [exclusionResult, setExclusionResult] = useState('');
  const [now, setNow] = useState(Date.now());
  const lifetime = useRef(new AbortController());
  const loadVersion = useRef(0);
  const detailVersion = useRef(0);
  const api = useCallback(() => createThreatAPI(tenant, lifetime.current.signal), [tenant]);
  useEffect(() => {
    if (lifetime.current.signal.aborted) lifetime.current = new AbortController();
    const controller = lifetime.current;
    const tick = window.setInterval(() => setNow(Date.now()), 30000);
    return () => { controller.abort(); loadVersion.current++; detailVersion.current++; window.clearInterval(tick); };
  }, []);

  const refresh = useCallback(async (more = false) => {
    const version = ++loadVersion.current;
    const signal = lifetime.current.signal;
    setLoading(true); setError('');
    try {
      const client = api();
      const [ipRows, banRows, sourceRows, exclusions] = await Promise.all([
        section === 'overview' || section === 'ips' ? client.ips(more ? cursor.current : '') : Promise.resolve(null),
        section === 'overview' || section === 'bans' ? client.bans() : Promise.resolve(null),
        section === 'overview' || section === 'ips' || section === 'sources' ? client.sources() : Promise.resolve(null),
        section === 'allowlist' ? client.allowlist() : Promise.resolve(null),
      ]);
      if (signal.aborted || version !== loadVersion.current) return;
      if (ipRows) {
        setIPs(old => more ? Array.from(new Map([...old, ...ipRows].map(ip => [ip.ip, ip])).values()) : ipRows);
        setHasMore(ipRows.length > 0);
        cursor.current = ipRows.at(-1)?.ip || '';
      }
      if (banRows) setBans(banRows);
      if (sourceRows) setSources(sourceRows);
      if (exclusions) setAllowlist(exclusions);
    } catch (err) { if (!signal.aborted && version === loadVersion.current) setError(message(err)); }
    finally { if (!signal.aborted && version === loadVersion.current) setLoading(false); }
  }, [api, section]);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (section !== 'bans' && section !== 'overview' && section !== 'sources') return;
    const timer = window.setInterval(() => { if (!busyRef.current) void refresh(); }, 60000);
    return () => window.clearInterval(timer);
  }, [refresh, section]);

  const inspect = useCallback(async (ip: string) => {
    const version = ++detailVersion.current;
    const signal = lifetime.current.signal;
    setSelectedIP(ip); setDetail(null); setDetailError(''); setDetailLoading(true);
    if (!validIP(ip)) { setDetailError('Enter a valid IPv4 or IPv6 address.'); setDetailLoading(false); return; }
    try {
      const result = await api().detail(ip);
      if (!signal.aborted && version === detailVersion.current) setDetail(result);
    } catch (err) { if (!signal.aborted && version === detailVersion.current) setDetailError(message(err)); }
    finally { if (!signal.aborted && version === detailVersion.current) setDetailLoading(false); }
  }, [api]);
  const routeIP = new URLSearchParams(navigation.path.split('?')[1] || '').get('ip') || '';
  const currentRouteIP = useRef(routeIP);
  currentRouteIP.current = routeIP;
  useEffect(() => {
    setDetailTab('summary');
    if (routeIP) void inspect(routeIP);
    else { detailVersion.current++; setSelectedIP(''); setDetail(null); setDetailError(''); }
  }, [routeIP, inspect]);
  const openIP = (ip: string) => {
    if (ip === routeIP) { void inspect(ip); return; }
    navigation.navigate(`/threat-protection/${section}?ip=${encodeURIComponent(ip)}`);
  };
  const operation = async (action: () => Promise<void>, formOperation = false) => {
    if (busyRef.current) return;
    const signal = lifetime.current.signal;
    busyRef.current = true; setBusy(true); setNotice(''); setError(''); setFormError('');
    try { await action(); }
    catch (err) { if (!signal.aborted) (formOperation ? setFormError : setError)(message(err)); }
    finally { if (!signal.aborted) { busyRef.current = false; setBusy(false); } }
  };
  const submit = () => {
    if (!form) return;
    const address = form.address.trim(); const reason = form.reason.trim();
    if (!(form.action.includes('allowlist') ? validNetwork(address) : validIP(address))) { setFormError('Enter a valid IP address or CIDR for this operation.'); return; }
    if (!reason) { setFormError('An administrative reason is required.'); return; }
    void operation(async () => {
      const signal = lifetime.current.signal;
      const client = api();
      if (form.action === 'ban') await client.ban(address, form.duration, reason);
      else if (form.action === 'unban') await client.unban(address, form.duration, reason);
      else await client.exclusion(address, reason, form.action === 'remove-allowlist');
      if (signal.aborted) return;
      setForm(null);
      setNotice(form.action === 'remove-allowlist' ? 'Removal processed. Operator-configured entries may remain in the refreshed list.' : 'Administrative change saved.');
      await refresh();
      if (!signal.aborted && selectedIP && currentRouteIP.current === selectedIP) await inspect(selectedIP);
    }, true);
  };
  const props: ThreatProtectionViewProps = {
    section, ips, bans: bans.filter(b => Date.parse(b.expires_at) > now), sources, allowlist, loading, busy, error, notice, now,
    query, status, hasMore, detail, detailLoading, detailError, selectedIP, detailTab, form, formError, exclusionIP, exclusionResult,
    onQuery: setQuery, onStatus: setStatus, onRefresh: () => { if (!busyRef.current) void refresh(); },
    onLoadMore: () => { if (!loading && hasMore && !busyRef.current) void refresh(true); },
    onLookup: () => { if (!validIP(query.trim())) { setError('Enter an exact IPv4 or IPv6 address to look up.'); return; } openIP(query.trim()); },
    onInspect: openIP, onCloseDetail: () => navigation.navigate(`/threat-protection/${section}`), onDetailTab: setDetailTab,
    onForm: (action, address = '') => { setForm({ action, address, duration: '24h', reason: '' }); setFormError(''); },
    onFormChange: setForm, onCloseForm: () => setForm(null), onSubmit: submit,
    onRecalculate: () => { void operation(async () => { const signal = lifetime.current.signal; await api().recalculate(selectedIP); if (signal.aborted) return; setNotice('Reputation recalculated.'); await refresh(); if (!signal.aborted && currentRouteIP.current === selectedIP) await inspect(selectedIP); }); },
    onSync: id => { void operation(async () => { const signal = lifetime.current.signal; await api().sync(id); if (signal.aborted) return; setNotice('Source synchronization completed.'); await refresh(); }); },
    onExport: format => { void operation(async () => {
      const signal = lifetime.current.signal;
      const content = format === 'text' ? await api().exportText() : JSON.stringify({ data: await api().bans() }, null, 2);
      if (signal.aborted) return;
      const url = URL.createObjectURL(new Blob([content], { type: format === 'text' ? 'text/plain' : 'application/json' }));
      const link = document.createElement('a'); link.href = url; link.download = `temporary-bans.${format === 'text' ? 'txt' : 'json'}`; link.click(); URL.revokeObjectURL(url);
    }); },
    onExclusionIP: value => { setExclusionIP(value); setExclusionResult(''); },
    onCheckExclusion: () => {
      if (!validIP(exclusionIP.trim())) { setExclusionResult('Enter a valid IPv4 or IPv6 address.'); return; }
      void operation(async () => { const signal = lifetime.current.signal; const result = await api().checkExclusion(exclusionIP.trim()); if (!signal.aborted) setExclusionResult(`${result.ip}: ${result.reason || 'No exclusion applies.'}`); });
    },
  };
  return <ThreatProtectionView {...props} />;
}
